import { motion } from 'framer-motion';
import { FrameworkContext } from 'global/cnr/contexts/FrameworkContent';
import { UiItemContext } from 'global/cnr/contexts/UiItemContext';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import { useImage } from 'global/cnr/contexts/useHelpers/useImage';
import { convertHelpers } from 'global/common/convert';
import { DataModificationsContext } from 'global/pageItem/modification/UiDataModifications';
import { dataModificationTypes } from 'global/viewSettings/enums/itemActionTypes';
import _ from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Icon, Image } from 'semantic-ui-react';
import { ImageContext } from '../../cnr/contexts/ImageContext';
import { ParentContext } from '../../cnr/contexts/ParentContext';
import { getLastSplit } from '../../common/filtering';
import UiSaveButtons from '../../components/buttons/UiSaveButtons';
import { appIconTypes } from '../../enums/appIconTypes';
import { gEnums } from '../../enums/globalEnums';
import UiConfirmation from '../../pageItem/modification/UiConfirmation';
import FullPageWrapper from '../../wrappers/FullPageWrapper';
import Wrapper, { wrapperTypes } from '../../wrappers/Wrapper';
import NoData from '../alerts/NoData';
import UploaderWithProvider from '../uploading/Uploader';
import { _longPressTypes } from 'global/wrappers/LongPressWrapper';
import DropdownOptionsMenu from '../forms/elements/DropdownOptionsMenu';
import { openExternal } from 'global/viewSettings/helpers/settingsLinks';
import { useStorage } from 'global/cnr/contexts/useHelpers/useStorage';
import { createRefPath, createRefPath_client } from 'global/firestoreData/appData/appRefPaths';
import { _cacheGroupTypes } from 'global/cnr/contexts/useHelpers/useTimestamp';
import { useStorageUpdate } from 'global/cnr/contexts/useHelpers/useStorageUpdate';

const FullImage = (props) => {

  const { _appImage, fullImageProps, handleClose, useFpw, viewOnly, imageProps } = props
  const { documentKey, customMetadata, path } = fullImageProps ?? {}
  const { uploadedBy } = customMetadata ?? {}
  const _fileName = path ? getLastSplit(path, '/') : null

  const appUserAccess = useAppUserAccess()
  const { accessLevel, email } = appUserAccess ?? {}

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers, fns, fn_cls } = parentContext ?? {}
  const { app_state, paps_state, currentSettings_state, storage_state } = states ?? {}
  const { mainStorage } = app_state ?? {}
  const { database_fns } = fns ?? {}
  const { pathViews } = paps_state ?? {}
  const { updating, confirmation } = storage_state ?? {}
  const { storage_handlers } = handlers ?? {}

  const { aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appUserCollection } = aps_appUserSettings ?? {}

  const frameworkContext = useContext(FrameworkContext)
  const { framework_state } = frameworkContext ?? {}
  const { currentLongPress } = framework_state ?? {}
  const { longPressData } = currentLongPress ?? {}
  const dataName_lp = convertHelpers.getItemName(longPressData)

  const dataModificationContext = useContext(DataModificationsContext)
  const { dataModifications_state } = dataModificationContext ?? {}
  const { dataModificationType } = dataModifications_state ?? {}

  const uiItemContext = useContext(UiItemContext);
  const { item_state } = uiItemContext ?? {}
  const { itemData: _itemData, _modifyActionProps, _imageOpts, view: view_key } = item_state ?? {}
  const { modifyItemData } = _modifyActionProps ?? {}
  const dataName = convertHelpers.getItemName(modifyItemData)

  const _itemData_image = longPressData || modifyItemData || _itemData

  const appImage = useImage({ itemData: _itemData_image, fullImage: false, _imageOpts, imageProps, _appImage })
  const _app_image = _appImage || appImage
  const { currentUrls, image_name, image_path } = _app_image ?? {}
  const { full, thumbnail } = currentUrls ?? {}

  const hasThumbnail = thumbnail ? true : false

  const { _dataModificationType, forClientStorage } = useMemo(() => {
    let _dtm = dataModificationType;
    let _forClientStorage = false;

    switch (currentLongPress?.longPressType) {
      case _longPressTypes.bannerImage:
        _dtm = dataModificationTypes.images
        break;
      case _longPressTypes.item:
        _dtm = dataModificationTypes.images
        _forClientStorage = appUserCollection === currentLongPress?.uivi
      default:
      // nothing
    }
    return { _dataModificationType: _dtm, forClientStorage: _forClientStorage }
  }, [dataModificationType, currentLongPress])

  const captions = useMemo(() => {
    const _topper2 = image_path || image_name || dataName || _fileName

    if (longPressData) {
      return {
        sbc: full ? 'Edit Image' : 'Add Image',
        dbc: 'Delete Image',
        non: 'No Current Image',
        topper: longPressData?.name || `${longPressData?.firstName} ${longPressData?.lastName}`,
        topper2: `Full Image (${longPressData?.email || longPressData?._itemKey})`
      }
    } else {
      switch (_dataModificationType) {
        case dataModificationTypes.images:
          return {
            sbc: full ? 'Edit Image' : 'Add Image',
            dbc: 'Delete Image',
            non: 'No Current Image',
            topper: image_name,
            topper2: _topper2
          }

        case dataModificationTypes.pdfs:
          return {
            sbc: full ? 'Edit PDF' : 'Add PDF',
            dbc: 'Delete PDF',
            non: 'No Current PDF',
            topper: 'PDF',
            topper2: _topper2
          }

        default:
          return {
            sbc: full ? 'Edit Image' : 'Add Image',
            dbc: 'Delete Image',
            non: 'No Current Image',
            topper: image_name,
            topper2: _topper2
          }
      }
    }


  }, [])

  // imageContext
  const imageContext = useContext(ImageContext)
  const { image_state } = imageContext ?? {}
  const { itemData } = image_state ?? {}
  const { email: email_image } = itemData ?? {}

  let allowMod = appUserAccess && (accessLevel > gEnums.accessLevels.admin.value)
  if (email_image && (email_image === email)) { allowMod = true }
  if (uploadedBy && (uploadedBy === email)) { allowMod = true }
  if (viewOnly) { allowMod = false }

  const { storageUrls } = useStorage({ image_name, forClientStorage, currentLongPress })
  const { removeItemsFromStorage } = useStorageUpdate({})

  // local state 
  const [allowUrlUpdate, setAllowUrlUpdate] = useState({ statusUrls: { full: true, thumbnail: true } })
  const [urls, setUrls] = useState()
  const [showDelete, setShowDelete] = useState()
  const [showUpload, setShowUpload] = useState()
  const [isLoaded, setIsLoaded] = useState(false);

  const { statusUrls } = allowUrlUpdate ?? {}

  const setSrc = (e, imageType) => {
    if (storageUrls) {
      if (imageType === 'full') {
        setUrls({ full: full, thumbnail: storageUrls.thumbnail })
      } else {
        setUrls({ full: storageUrls.full, thumbnail: thumbnail })
      }
    }
  }

  const handleImageLoad = () => setIsLoaded(true);
  const handleShowDelete = () => setShowDelete(!showDelete)
  const handleShowUpload = () => setShowUpload(!showUpload)

  const handleShowCreateThumbnail = async () => {
    const cb = (res) => {
      console.log('res', res)
    }
    const rez = await fn_cls.updateImageThumbnails(pathViews, cb, image_name)
  }

  const handleDeleteStorageItem = () => {
    setShowDelete()
    storage_handlers.handleStartUpdate()
    // removeItemsFromStorage()
    storage_handlers.handleDelete_storageItem(fullImageProps)
  }

  const handleOpenDb = () => {
    if (forClientStorage) {
      const _pathViews = {
        clients: pathViews.clients
      }
      openExternal.db(_pathViews, false, ['profileGallery', 'galleryItems'])
    } else {
      openExternal.db(pathViews, false, ['_assetUrls', 'pages'])
    }
  }

  const handleOpenStorage = () => {
    if (forClientStorage) {
      const _pathViews = {
        clients: pathViews.clients
      }
      openExternal.db(_pathViews, true, ['profiles', image_name])
    } else {
      openExternal.db(pathViews, true, ['pages', currentLongPress?.uivi, currentLongPress?.uiviKey])
    }

  }

  const handleUpdateProfileUrls = async () => {
    const refPath = createRefPath_client(pathViews, ['profileGallery', 'galleryItems'])
    const dataToUpdate = {
      [image_name]: {
        customMetadata: { uploadedBy: uploadedBy || 'admin' },
        urls: {
          full: storageUrls.full,
          thumbnail: storageUrls.thumbnail,
        },
      }
    }
    await database_fns.doc_set(refPath, dataToUpdate, true)
    const ts_update = Date.now()
    const refPath_ts = createRefPath(['_timeStamps', 'ts']);
    const dataToUpdate_ts = {
      [_cacheGroupTypes.appUsersInfo]: {
        [pathViews.events]: ts_update
      }
    }
    // const refPath_ts = createRefPath(['_timeStamps', _cacheGroupTypes.appUsersInfo]);
    // const dataToUpdate_ts = {
    //   [pathViews.events]: ts_update
    // }
    database_fns.doc_set(refPath_ts, dataToUpdate_ts, true)
  }

  const compareUrls = (urls_storage, urls_current) => {
    const statusUrls = {
      full: urls_storage?.full === urls_current?.full,
      thumbnail: urls_storage?.thumbnail === urls_current?.thumbnail
    };
    const needsUpdate = !statusUrls.full || !statusUrls.thumbnail;
    const needsThumbnail = !urls_storage?.thumbnail
    return { statusUrls, needsUpdate, needsThumbnail };
  };

  useEffect(() => {
    if (storageUrls && currentUrls) {
      const url_compares = compareUrls(storageUrls, currentUrls)
      if (url_compares.needsUpdate) {
        setAllowUrlUpdate(url_compares)
      } else {
        setAllowUrlUpdate(url_compares)
      }
      setUrls(currentUrls)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageUrls, currentUrls]);

  /**
   * This will only appear if allowMods i true
   * @returns saveButtons
   */
  const footer = () => {

    const btns_dd = []
    btns_dd.push({ oc: handleShowDelete, caption: captions.dbc, icon: appIconTypes.delete })
    btns_dd.push({ oc: handleOpenDb, caption: 'Open Asset Urls DB', icon: 'external alternate' })
    btns_dd.push({ oc: handleOpenStorage, caption: 'Open Storage', icon: 'external alternate' })
    allowUrlUpdate?.needsThumbnail && btns_dd.push({ oc: handleShowCreateThumbnail, caption: 'Create Thumbnail', icon: 'arrow right' })
    allowUrlUpdate?.needsUpdate && btns_dd.push({ oc: handleUpdateProfileUrls, caption: 'Update Profile Urls', icon: 'save' })

    return <UiSaveButtons
      ddb={btns_dd.length > 0 && <DropdownOptionsMenu menuButtons={btns_dd} caption={'Options'} icon={'list'} color={'blue'} float={'left'} />}
      save={{ oc: handleShowUpload, caption: captions.sbc, icon: appIconTypes.arrowRight, color: 'blue' }}
    />
  }

  const handleOpen = (url) => {
    window.open(url, '_blank');
  };

  const iconGroup = (size, hasStorageUrl) => <Icon.Group size={size}>
    <Icon name='user' color={hasStorageUrl ? 'green' : 'grey'} />
    <Icon corner name={hasStorageUrl ? 'check' : 'question'} color='grey' />
  </Icon.Group>

  const image_full = (src, cn) => {
    const _cn = !statusUrls?.full ? `${cn} nsu` : cn
    return <Image
      className={_cn}
      style={{ display: isLoaded ? 'block' : 'none' }}
      onLoad={handleImageLoad}
      src={src}
      size='medium'
      onError={(e) => setSrc(e, 'full')}   // fallback
    />
  }

  const image_thumbnail = (src, cn) => {
    const _cn = !statusUrls?.thumbnail ? `${cn} nsu` : cn
    return <Image
      className={_cn}
      style={{ display: isLoaded ? 'block' : 'none' }}
      onLoad={handleImageLoad}
      src={src}
      size='tiny'
      onError={(e) => setSrc(e, 'thumbnail')}   // fallback
    />
  }

  const content = () => {
    const { full: url_full, thumbnail: url_thumbnail } = urls ?? {}
    const src = url_full || url_thumbnail
    let cn_full = 'img-full cntr';
    let cn_tb = 'img-tbn cntr'
    if (!hasThumbnail) {
      cn_full += ' ntb'
    } else {
      cn_full += ' htb'
    }
    return <Wrapper
      content={src ? <motion.div
        style={{ height: '100%', gap: '1em', display: 'flex', textAlign: 'center', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div style={{ gap: '1em', display: 'flex', textAlign: 'center', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
          <div onClick={() => handleOpen(url_full)} style={{ fontSize: '.6rem', paddingLeft: '2em', paddingRight: '2em' }}>
            {url_full ? image_full(url_full, cn_full) : iconGroup('massive', storageUrls?.full)}
          </div>
          <div onClick={() => handleOpen(url_thumbnail)} style={{ fontSize: '.6rem', paddingLeft: '2em', paddingRight: '2em' }}>
            {url_thumbnail ? image_thumbnail(url_thumbnail, cn_tb) : iconGroup('huge', storageUrls?.thumbnail)}
          </div>
        </div>
      </motion.div> : <NoData fullCaption={captions.non} />}
      footer={allowMod && footer()}
      wrapperType={wrapperTypes.paddedHeader}
      updating={updating}
      confirmation={confirmation}
      css={{ container: 'full-image app-dark-mode' }}
    />
  }

  const fpw = () => <FullPageWrapper
    content={content()}
    handleCancel={handleClose}
    topperCaption={documentKey ? _.startCase(documentKey) : captions.topper}
    topperCaption2={captions.topper2}
  />

  const fpw_none = () => <FullPageWrapper
    content={content()}
    handleCancel={handleClose}
    topperCaption={documentKey ? _.startCase(documentKey) : captions.topper}
    topperCaption2={captions.topper2}
  />

  if (showDelete) {
    return <UiConfirmation
      caption={'Delete Item?'}
      text={'Are you sure you want to delete this item. This action cannot be undone.'}
      updateCaption={'Delete'}
      updateColor={'red'}
      updateIcon={'delete'}
      handleUpdate={handleDeleteStorageItem}
      handleCancel={handleShowDelete}
    />
  } else if (showUpload) {
    return <UploaderWithProvider
      handleCancel={handleShowUpload}
      itemData={{}}
      origin={'FullImage'}
      storageLocationType={gEnums.storageLocationTypes.pages}
      appImage={_app_image}
      useFull={true}
    />
  } else {
    if (urls && storageUrls && allowUrlUpdate) {
      return handleClose || useFpw ? fpw() : content()
    } else {
      return fpw_none()
    }
  }

}

export default FullImage